import { isDefined } from "@clipboard-health/util-ts";
import { type Session } from "@src/appV2/Auth/types";
import {
  PricingType,
  VisitType,
  WorkerRequirements,
} from "@src/appV2/ExperimentalHomeHealth/Cases/types";
import {
  type AccountPricing,
  type AccountsPricingResponse,
} from "@src/appV2/ExperimentalHomeHealth/Cases/types/accountPricing";
import { FacilityRoles, FacilityUserPermissions } from "@src/appV2/FacilityUser/types";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";
import {
  type CbhFeatureFlags,
  type VisitRecommendedRateParameters,
} from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { clamp } from "lodash";

import { getFallbackRecommendedRate } from "./fallbackRates";

export type VisitRateSettingConfig =
  CbhFeatureFlags[typeof CbhFeatureFlag.CONFIGURE_VISIT_RATE_SETTING];

export type RateConfiguration =
  | {
      canSetRates: false;
    }
  | {
      canSetRates: true;
      takePercent: number;
      chargeRate: {
        recommendedRate: number;
        minimum: number;
        maximum: number;
      };
      payRate: {
        recommendedRate: number;
        minimum: number;
        maximum: number;
      };
    };

export function getRateConfiguration(params: {
  visitRateSettingConfig: VisitRateSettingConfig;
  session: Session;
  accountPricingList: AccountsPricingResponse | undefined;
  workerRequirements: WorkerRequirements | undefined;
  pricingType: PricingType | undefined;
  visitType: VisitType | undefined;
  patientAddress: string | undefined;
}): RateConfiguration {
  const {
    visitRateSettingConfig,
    session,
    accountPricingList,
    workerRequirements,
    visitType,
    pricingType,
    patientAddress,
  } = params;

  if (
    !canSetRates(session) ||
    !isDefined(workerRequirements) ||
    !isDefined(visitType) ||
    !isDefined(pricingType)
  ) {
    return { canSetRates: false };
  }

  // Recommended rates prefer an explicit account-pricing entry for this worker
  // requirement / visit type / pricing type. When none exists we fall back to the
  // configured default: the rate sheet (rate-configuration.json) when useRateSheet
  // is set, otherwise the flag's configured recommended rate.
  const accountPricing = accountPricingList?.data.find(
    (item) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      item.attributes.agentReq === workerRequirements &&
      item.attributes.visitType === visitType &&
      item.attributes.pricingType === pricingType
  );

  const recommendedChargeRateParams = visitRateSettingConfig.useRateSheet
    ? getFallbackRecommendedRate(patientAddress)
    : (visitRateSettingConfig.recommendedChargeRate ?? getFallbackRecommendedRate(patientAddress));

  const rateParams = {
    recommendedChargeRate: recommendedChargeRateParams,
    accountPricing,
    workerRequirements,
    visitType,
    pricingType,
  };

  const rateConfiguration = {
    canSetRates: true,
    takePercent: visitRateSettingConfig.takePercent,
    chargeRate: {
      recommendedRate: getRecommendedChargeRate(rateParams),
      minimum: minimumChargeRate(visitRateSettingConfig, pricingType),
      maximum: maximumChargeRate(visitRateSettingConfig, pricingType),
    },
    payRate: {
      recommendedRate: getRecommendedPayRate(visitRateSettingConfig, rateParams),
      minimum: minimumPayRate(visitRateSettingConfig, pricingType),
      maximum: maximumPayRate(visitRateSettingConfig, pricingType),
    },
  };

  rateConfiguration.chargeRate.recommendedRate = clamp(
    rateConfiguration.chargeRate.recommendedRate,
    rateConfiguration.chargeRate.minimum,
    rateConfiguration.chargeRate.maximum
  );

  rateConfiguration.payRate.recommendedRate = clamp(
    rateConfiguration.payRate.recommendedRate,
    rateConfiguration.payRate.minimum,
    rateConfiguration.payRate.maximum
  );

  return rateConfiguration;
}

export function canSetRates(session: Session): boolean {
  if (session.admin) {
    return true;
  }

  if (!isDefined(session.user)) {
    return false;
  }

  if (session.user.permissions?.includes(FacilityUserPermissions.SET_IN_HOME_VISIT_RATE)) {
    return true;
  }

  return session.user.access.includes(FacilityRoles.ADMIN);
}

type RateParams = {
  recommendedChargeRate: VisitRecommendedRateParameters;
  accountPricing: AccountPricing | undefined;
  workerRequirements: WorkerRequirements;
  visitType: VisitType;
  pricingType: PricingType;
};

function getRecommendedChargeRate({
  recommendedChargeRate,
  accountPricing,
  workerRequirements,
  visitType,
  pricingType,
}: RateParams): number {
  if (isDefined(accountPricing)) {
    return accountPricing.attributes.chargeRateInMinorUnits / 100;
  }

  if (pricingType === PricingType.PER_HOUR) {
    const rates = recommendedChargeRate.perHour;
    switch (workerRequirements) {
      case WorkerRequirements.PT:
      case WorkerRequirements.PTA:
      case WorkerRequirements.OT:
      case WorkerRequirements.OTA:
      case WorkerRequirements.MSW:
      case WorkerRequirements.RN: {
        return rates.RN / 100;
      }
      case WorkerRequirements.LVN: {
        return rates.LVN / 100;
      }
      case WorkerRequirements.CNA: {
        return rates.CNA / 100;
      }
      case WorkerRequirements.CAREGIVER: {
        return rates.Caregiver / 100;
      }
      case WorkerRequirements.CHHA: {
        return rates.CHHA / 100;
      }
      default: {
        return rates.RN / 100;
      }
    }
  }

  const rates = recommendedChargeRate.perVisit;
  switch (workerRequirements) {
    case WorkerRequirements.PT:
    case WorkerRequirements.PTA:
    case WorkerRequirements.OT:
    case WorkerRequirements.OTA:
    case WorkerRequirements.MSW:
    case WorkerRequirements.RN: {
      return recommendedRnChargeRate(visitType, rates);
    }
    case WorkerRequirements.LVN: {
      return rates.LVN / 100;
    }
    case WorkerRequirements.CNA: {
      return rates.CNA / 100;
    }
    case WorkerRequirements.CAREGIVER: {
      return rates.Caregiver / 100;
    }
    case WorkerRequirements.CHHA: {
      return rates.CHHA / 100;
    }
    default: {
      return rates["RN-Regular"] / 100;
    }
  }
}

function recommendedRnChargeRate(
  visitType: VisitType,
  perVisitRates: VisitRecommendedRateParameters["perVisit"]
) {
  switch (visitType) {
    case VisitType.ADMISSION: {
      return perVisitRates["RN-SOC"] / 100;
    }
    case VisitType.REGULAR:
    case VisitType.EVALUATION: {
      return perVisitRates["RN-Regular"] / 100;
    }
    case VisitType.ADMISSION_AND_FIRST_VISIT: {
      return perVisitRates["RN-SOC-And-Regular"] / 100;
    }
    case VisitType.RESUMPTION_OF_CARE: {
      return perVisitRates["RN-ROC"] / 100;
    }
    case VisitType.RECERTIFICATION: {
      return perVisitRates["RN-Recert"] / 100;
    }
    case VisitType.DISCHARGE: {
      return perVisitRates["RN-Discharge"] / 100;
    }
    case VisitType.SUPERVISORY: {
      return perVisitRates["RN-Supervisory"] / 100;
    }
    default: {
      return perVisitRates["RN-Regular"] / 100;
    }
  }
}

function getRecommendedPayRate(config: VisitRateSettingConfig, params: RateParams): number {
  const takeRate = config.takePercent / 100;
  return Number((getRecommendedChargeRate(params) * (1 - takeRate)).toFixed(2));
}

function minimumChargeRate(config: VisitRateSettingConfig, pricingType: PricingType): number {
  if (pricingType === PricingType.PER_HOUR) {
    return config.minChargeRate.perHour / 100;
  }

  return config.minChargeRate.perVisit / 100;
}

function maximumChargeRate(config: VisitRateSettingConfig, pricingType: PricingType): number {
  if (pricingType === PricingType.PER_HOUR) {
    return config.maxChargeRate.perHour / 100;
  }

  return config.maxChargeRate.perVisit / 100;
}

function minimumPayRate(config: VisitRateSettingConfig, pricingType: PricingType): number {
  const takeRate = config.takePercent / 100;
  return Number((minimumChargeRate(config, pricingType) * (1 - takeRate)).toFixed(2));
}

function maximumPayRate(config: VisitRateSettingConfig, pricingType: PricingType): number {
  const takeRate = config.takePercent / 100;
  return Number((maximumChargeRate(config, pricingType) * (1 - takeRate)).toFixed(2));
}
